import { FirebaseError } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  User,
  signInAnonymously,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  linkWithPopup,
  Auth,
  signInWithCredential
} from 'firebase/auth';
import { runTransaction, doc, collection, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { app, db } from './firebase/config';
import { getFunctions } from 'firebase/functions';

// Initialize Firebase
const auth = getAuth(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// Configure provider settings
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

export const signInWithGoogle = async () => {
  try {
    let result;
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      result = await convertAnonymousUserFromGoogleProvider();
    } else {
      result = await signInWithPopup(auth, googleProvider);
    }
    return result.user;
  } catch (popupError: any) {
    if (popupError.code === 'auth/popup-blocked') {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    if (
      popupError.code === 'auth/credential-already-in-use' &&
      auth.currentUser
    ) {
      const result = await handleExistingCredential(
        popupError,
        auth.currentUser,
        auth
      );
      return result.user;
    }
    throw popupError;
  }
};

export const handleRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    return result?.user || null;
  } catch (error) {
    console.error('Error handling redirect:', error);
    return null;
  }
};

export const signInWithEmailPassword = async (
  email: string,
  password: string
) => {
  try {
    const result = await firebaseSignInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return result.user;
  } catch (error: any) {
    if (
      error.code === 'auth/user-not-found' ||
      error.code === 'auth/wrong-password'
    ) {
      throw new Error('Invalid email or password');
    }
    throw error;
  }
};

export const registerWithEmailPassword = async (
  email: string,
  password: string
) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('Email already in use');
    }
    throw error;
  }
};

export const signOutUser = () => signOut(auth);

export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

interface ErrorDetails {
  code: string;
  message: string;
  details?: Record<string, any>;
}

class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: Record<string, any>
  ) {
    super(message);
    this.name = 'AppError';
  }

  toJSON(): ErrorDetails {
    return {
      code: this.code,
      message: this.message,
      details: this.details
    };
  }
}

class AuthError extends AppError {
  constructor(message: string, details?: Record<string, any>) {
    super(message, 'AUTH_ERROR', details);
    this.name = 'AuthError';
  }
}

function handleAuthError(error: unknown): AuthError {
  if (error instanceof AuthError) {
    return error;
  }

  if (error instanceof FirebaseError && error.code.startsWith('auth/')) {
    return new AuthError(getAuthErrorMessage(error.code), {
      originalError: error
    });
  }

  return new AuthError('Authentication failed', { originalError: error });
}

async function signInAsAnonymous(): Promise<User> {
  try {
    const { user } = await signInAnonymously(auth);
    return user;
  } catch (error) {
    throw handleAuthError(error);
  }
}

function getAuthErrorMessage(code: string): string {
  const messages: Record<string, string> = {
    'auth/email-already-in-use': 'An account with this email already exists',
    'auth/invalid-email': 'Invalid email address',
    'auth/operation-not-allowed': 'Operation not allowed',
    'auth/weak-password': 'Password is too weak',
    'auth/user-disabled': 'This account has been disabled',
    'auth/user-not-found': 'No account found with this email',
    'auth/wrong-password': 'Incorrect password',
    'auth/popup-closed-by-user': 'Sign in was cancelled',
    'auth/cancelled-popup-request': 'Sign in was cancelled',
    'auth/popup-blocked': 'Sign in popup was blocked by your browser'
  };

  return messages[code] || 'Authentication failed';
}

async function convertAnonymousUserFromGoogleProvider() {
  const currentUser = auth.currentUser;

  if (!currentUser?.isAnonymous) {
    throw new Error('User is not anonymous');
  }

  try {
    const result = await linkWithPopup(currentUser, googleProvider);
    await transferUserData(currentUser.uid, result.user.uid);
    return result;
  } catch (error) {
    throw error;
  }
}

interface ConversionResult {
  user: User;
  isNewUser: boolean;
  previousUid?: string;
}

const handleExistingCredential = async (
  error: AuthError,
  currentUser: User,
  auth: Auth
): Promise<ConversionResult> => {
  const existingCredential = GoogleAuthProvider.credentialFromError(error);

  if (!existingCredential) {
    throw new Error('No existing credential found');
  }

  try {
    const signInResult = await signInWithCredential(auth, existingCredential);
    const previousUid = currentUser.uid;

    await transferUserData(previousUid, signInResult.user.uid);

    return {
      user: signInResult.user,
      isNewUser: false,
      previousUid
    };
  } catch (signInError) {
    throw new Error(
      `Error signing in with existing Google account: ${signInError}`
    );
  }
};

const transferUserData = async (
  anonymousUid: string,
  permanentUid: string
): Promise<void> => {
  try {
    if (!anonymousUid || !permanentUid) {
      throw new Error('Both anonymousUid and permanentUid are required');
    }

    if (anonymousUid === permanentUid) {
      throw new Error('Source and destination UIDs cannot be the same');
    }

    const subcollections = ['subscriptions', 'payments', 'checkout_sessions'];

    await runTransaction(db, async (transaction) => {
      // Transfer main user document
      const anonymousUserRef = doc(db, 'customers', anonymousUid);
      const anonymousUserDoc = await transaction.get(anonymousUserRef);

      if (!anonymousUserDoc.exists()) {
        throw new Error(
          `No user data found for anonymous UID: ${anonymousUid}`
        );
      }

      const permanentUserRef = doc(db, 'customers', permanentUid);
      const permanentUserDoc = await transaction.get(permanentUserRef);

      if (permanentUserDoc.exists()) {
        throw new Error(
          `User data already exists for permanent UID: ${permanentUid}`
        );
      }

      const userData = anonymousUserDoc.data();
      const updatedUserData = {
        ...userData,
        uid: permanentUid,
        lastUpdated: new Date()
      };

      // Set main user document
      transaction.set(permanentUserRef, updatedUserData);

      // Transfer subcollections
      for (const subcollName of subcollections) {
        const subcollRef = collection(
          db,
          'customers',
          anonymousUid,
          subcollName
        );
        const subcollDocs = await getDocs(subcollRef);

        subcollDocs.forEach((docSnapshot) => {
          const newDocRef = doc(
            db,
            'customers',
            permanentUid,
            subcollName,
            docSnapshot.id
          );
          transaction.set(newDocRef, {
            ...docSnapshot.data(),
            userId: permanentUid,
            lastUpdated: new Date()
          });

          const oldDocRef = doc(
            db,
            'customers',
            anonymousUid,
            subcollName,
            docSnapshot.id
          );
          transaction.delete(oldDocRef);
        });
      }

      // Delete the old user document after all subcollections are transferred
      transaction.delete(anonymousUserRef);
    });
  } catch (error) {
    if (error instanceof FirebaseError) {
      console.error(`Firebase error during transfer: ${error.code}`, error);
      throw error;
    }

    console.error('Error during user data transfer:', error);
    throw error;
  }
};

export async function uploadResumeToStorage(
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  const storage = getStorage();
  const storageRef = ref(storage, `resumes/${Date.now()}_${file.name}`);
  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(progress);
      },
      (error) => {
        console.error('Error uploading resume:', error);
        reject(error);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        } catch (error) {
          console.error('Error getting download URL:', error);
          reject(error);
        }
      }
    );
  });
}

export { signInAsAnonymous, functions };
